import type { CreateBusinessProcessParams, CreateProjectParams, UpdateBusinessProcessParams, UpdateProjectParams, UploadDocumentParams } from '@/domain/models/project.js';
import type {
    CreateBusinessProcess,
    CreateProject,
    DeleteBusinessProcess,
    DeleteProjectDocument,
    GetProjectDocumentDownloadUrl,
    ListActiveBusinessProcesses,
    ListBusinessProcesses,
    ListProjectDocuments,
    ListProjectUsers,
    LoadMyProjects,
    LoadProjectById,
    LoadProjects,
    ToggleBusinessProcess,
    UpdateBusinessProcess,
    UpdateProject,
    UpdateProjectStatus,
    UploadProjectDocument
} from '@/domain/use-cases/projects.js';
import { BaseControllerImpl } from '@/presentation/controllers/base/implementation.js';
import type { ProjectStatus } from '@/shared/utils/format-status.js';

export class ProjectController extends BaseControllerImpl {
    constructor(
        private readonly loadProjectsUseCase: LoadProjects,
        private readonly loadProjectByIdUseCase: LoadProjectById,
        private readonly createProjectUseCase: CreateProject,
        private readonly updateProjectUseCase: UpdateProject,
        private readonly updateProjectStatusUseCase: UpdateProjectStatus,
        private readonly loadMyProjectsUseCase: LoadMyProjects,
        private readonly listBusinessProcessesUseCase: ListBusinessProcesses,
        private readonly listActiveBusinessProcessesUseCase: ListActiveBusinessProcesses,
        private readonly listProjectUsersUseCase: ListProjectUsers,
        private readonly createBusinessProcessUseCase: CreateBusinessProcess,
        private readonly updateBusinessProcessUseCase: UpdateBusinessProcess,
        private readonly toggleBusinessProcessUseCase: ToggleBusinessProcess,
        private readonly deleteBusinessProcessUseCase: DeleteBusinessProcess,
        private readonly listDocumentsUseCase: ListProjectDocuments,
        private readonly uploadDocumentUseCase: UploadProjectDocument,
        private readonly deleteDocumentUseCase: DeleteProjectDocument,
        private readonly getDocumentDownloadUrlUseCase: GetProjectDocumentDownloadUrl
    ) {
        super();
    }

    public async loadProjects(params?: { search?: string; status?: string; clientId?: string }) {
        return this.loadProjectsUseCase.execute(params);
    }

    public async loadProjectById(id: string) {
        return this.loadProjectByIdUseCase.execute(id);
    }

    public async createProject(params: CreateProjectParams) {
        return this.createProjectUseCase.execute(params);
    }

    public async updateProject(id: string, params: UpdateProjectParams) {
        return this.updateProjectUseCase.execute(id, params);
    }

    public async updateProjectStatus(id: string, status: ProjectStatus) {
        return this.updateProjectStatusUseCase.execute(id, status);
    }

    public async loadMyProjects() {
        return this.loadMyProjectsUseCase.execute();
    }

    public async listBusinessProcesses(projectId: string) {
        return this.listBusinessProcessesUseCase.execute(projectId);
    }

    public async listActiveBusinessProcesses(projectId: string) {
        return this.listActiveBusinessProcessesUseCase.execute(projectId);
    }

    public async listProjectUsers(projectId: string, profile?: string) {
        return this.listProjectUsersUseCase.execute(projectId, profile);
    }

    public async createBusinessProcess(projectId: string, params: CreateBusinessProcessParams) {
        return this.createBusinessProcessUseCase.execute(projectId, params);
    }

    public async updateBusinessProcess(projectId: string, processId: string, params: UpdateBusinessProcessParams) {
        return this.updateBusinessProcessUseCase.execute(projectId, processId, params);
    }

    public async toggleBusinessProcess(projectId: string, processId: string, isActive: boolean) {
        return this.toggleBusinessProcessUseCase.execute(projectId, processId, isActive);
    }

    public async deleteBusinessProcess(projectId: string, processId: string) {
        return this.deleteBusinessProcessUseCase.execute(projectId, processId);
    }

    public async listDocuments(projectId: string) {
        return this.listDocumentsUseCase.execute(projectId);
    }

    public async uploadDocument(projectId: string, params: UploadDocumentParams) {
        return this.uploadDocumentUseCase.execute(projectId, params);
    }

    public async deleteDocument(projectId: string, docId: string) {
        return this.deleteDocumentUseCase.execute(projectId, docId);
    }

    public async getDocumentDownloadUrl(projectId: string, docId: string) {
        return this.getDocumentDownloadUrlUseCase.execute(projectId, docId);
    }
}
